import {
    forcedCurrencyCodeForLang,
    shouldHideCurrencyConverter,
    FORCED_CURRENCY_CODE,
} from '../../utils/hideCurrencyConverter.js';

function normalizeCurrencyCode(input) {
    if (input && typeof input === 'object') {
        return String(input.code ?? '').trim().toUpperCase() || null;
    }
    const code = String(input ?? '').trim().toUpperCase();
    return code || null;
}

export const currencyModule = {
    namespaced: true,
    state: {
        selectedCurrencyCode: null,
        lockedToEuro: false,
    },
    getters: {
        isLockedToEuro: (state, getters, rootState) => {
            return state.lockedToEuro
                || !!rootState?.euroOnlyCurrency
                || !!rootState?.secondHomeFunnel;
        },
        isCurrencySelectorHidden: (state, getters, rootState) => {
            return getters.isLockedToEuro
                || shouldHideCurrencyConverter(rootState?.lang);
        },
        currentCurrency: (state, getters, rootState) => {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const hideConverter = shouldHideCurrencyConverter(rootState?.lang);
            const forcedCode = forcedCurrencyCodeForLang(rootState?.lang);
            const selectedCode = hideConverter && forcedCode
                ? forcedCode
                : String(state.selectedCurrencyCode ?? '').trim().toUpperCase();
            const selected = list.find((item) => String(item?.code ?? '').trim().toUpperCase() === selectedCode);
            const fallback = selected || list[0] || {};
            const code = String((hideConverter && forcedCode) ? forcedCode : (fallback?.code ?? '')).trim().toUpperCase();

            return {
                ...fallback,
                code,
                symbol: String(fallback?.symbol ?? '').trim(),
                conversion: 1,
            };
        }
    },
    mutations: {
        SET_EURO_LOCK(state, locked) {
            state.lockedToEuro = !!locked;
        },
        SET_CURRENCY(state, currency) {
            if (state.lockedToEuro) {
                state.selectedCurrencyCode = 'EUR';
                if (typeof window !== 'undefined' && window.localStorage) {
                    localStorage.setItem('currency', 'EUR');
                }
                return;
            }
            const code = normalizeCurrencyCode(currency);
            state.selectedCurrencyCode = code;

            if (typeof window !== 'undefined' && window.localStorage) {
                if (code) {
                    localStorage.setItem('currency', code);
                } else {
                    localStorage.removeItem('currency');
                }
            }
        },
        SET_CURRENCY_CODE(state, code) {
            const normalizedCode = state.lockedToEuro
                ? 'EUR'
                : normalizeCurrencyCode(code);
            state.selectedCurrencyCode = normalizedCode;

            if (typeof window !== 'undefined' && window.localStorage) {
                if (normalizedCode) {
                    localStorage.setItem('currency', normalizedCode);
                } else {
                    localStorage.removeItem('currency');
                }
            }
        }
    },
    actions: {
        lockToEuro({ commit }) {
            commit('SET_EURO_LOCK', true);
            commit('SET_CURRENCY_CODE', 'EUR');
        },

        unlockEuroLock({ commit }) {
            commit('SET_EURO_LOCK', false);
        },

        initCurrency({ commit, rootState }) {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const firstCode = normalizeCurrencyCode(list[0]);
            const hideConverter = shouldHideCurrencyConverter(rootState?.lang);
            let storedCode = null;

            if (!hideConverter && typeof window !== 'undefined' && window.localStorage) {
                const raw = localStorage.getItem('currency');
                if (raw) {
                    storedCode = normalizeCurrencyCode(raw);
                }
            }

            const isValidStored = list.some((item) => normalizeCurrencyCode(item) === storedCode);
            if (isValidStored) {
                commit('SET_CURRENCY_CODE', storedCode);
                return;
            }

            if (hideConverter) {
                const forcedCode = forcedCurrencyCodeForLang(rootState?.lang) || FORCED_CURRENCY_CODE;
                const found = list.find((item) => normalizeCurrencyCode(item) === forcedCode);
                commit('SET_CURRENCY_CODE', found ? forcedCode : firstCode);
                return;
            }

            commit('SET_CURRENCY_CODE', firstCode);
        },

        setCurrencyByCode({ commit, dispatch, rootState, state }, payload) {
            const code = typeof payload === 'object' && payload !== null ? payload.code : payload;
            const skipCatalogRefresh = typeof payload === 'object' && payload !== null && payload.skipCatalogRefresh;
            const previousCode = normalizeCurrencyCode(state.selectedCurrencyCode);

            if (state.lockedToEuro || rootState?.euroOnlyCurrency || rootState?.secondHomeFunnel) {
                commit('SET_EURO_LOCK', true);
                commit('SET_CURRENCY_CODE', 'EUR');
                return;
            }
            if (shouldHideCurrencyConverter(rootState?.lang)) {
                const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
                const forcedCode = forcedCurrencyCodeForLang(rootState?.lang) || FORCED_CURRENCY_CODE;
                const found = list.find((item) => normalizeCurrencyCode(item) === forcedCode);
                commit('SET_CURRENCY_CODE', found ? forcedCode : normalizeCurrencyCode(list[0]));
            } else {
                const targetCode = String(code ?? '').trim().toUpperCase();
                const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
                const firstCode = normalizeCurrencyCode(list[0]);
                const found = list.find((item) => normalizeCurrencyCode(item) === targetCode);
                commit('SET_CURRENCY_CODE', found ? targetCode : firstCode);
            }

            if (!skipCatalogRefresh) {
                const nextCode = normalizeCurrencyCode(state.selectedCurrencyCode);
                const catalogCode = rootState?.form?.productsCurrencyCode ?? null;
                const currencyChanged = previousCode && nextCode && previousCode !== nextCode;
                const catalogMismatch = nextCode && catalogCode && nextCode !== catalogCode;
                if (currencyChanged || catalogMismatch) {
                    dispatch('form/reloadProductsCatalog', null, { root: true }).catch(() => {});
                }
            }
        }
    }
};
